import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Col, Row } from 'react-bootstrap-5';

import PageLibrary from './page_library';
import DefaultPages from './default_pages';
import TemplateDefaultPages from './template_default_pages';
import ToggleOverrideSection from '../shared/toggle_override_section';
import {
  addDefaultPage,
  clearDefaultPages,
  clearPageLibrary,
  removeDefaultPage,
  removeLibraryPage,
  reorderDefaultPage,
  reorderLibraryPage,
  setDefaultPages,
  setLibraryPages
} from '../store/actions';


import AvailablePages from './available_pages';
import { nanoid } from 'nanoid';

export function PagePicker({
  addDefaultPage,
  availablePages,
  clearDefaultPages,
  clearPageLibrary,
  defaultPages,
  formData,
  removeDefaultPage,
  removeLibraryPage,
  reorderDefaultPage,
  reorderLibraryPage,
  setDefaultPages,
  setLibraryPages,
  templateDefaultPageLibrary,
  templateDefaultPages,
  userCreated
}) {

  const handleUseDefaults = () => {
    clearDefaultPages();
    clearPageLibrary();
  };

  const handleOverrideDefaults = () => {
    setDefaultPages([...templateDefaultPages]);
    setLibraryPages(templateDefaultPageLibrary.map(page => ({ id: page.id, key: nanoid() })));
  };

  const locked = !(defaultPages || userCreated);

  return (
    <div>
      {!userCreated && (
        <ToggleOverrideSection
          checked={!defaultPages}
          className="border-bottom pb-3"
          name="page-option-override"
          subtitle={!defaultPages ?
            'Unchecking will allow you to customize which pages appear in this document' :
            'Checking this will use Buildout\'s default pages for this document'
          }
          title="Use Buildout Default Pages"
          onOverride={handleOverrideDefaults}
          onRemove={handleUseDefaults}
        />
      )}
      <AvailablePages
        locked={locked}
        onOverride={handleOverrideDefaults}
        onUseDefaults={handleUseDefaults}
      />
      <Row>
        <Col md={6}>
          <PageLibrary
            addDefaultPage={addDefaultPage}
            availablePages={availablePages}
            locked={locked}
            pages={formData.availablePageTemplateTreeIds}
            removeLibraryPage={removeLibraryPage}
            templateDefaultPageLibrary={templateDefaultPageLibrary}
            onReorderPage={reorderLibraryPage}
          />
        </Col>
        <Col md={6}>
          {locked && (
            <TemplateDefaultPages
              availablePages={availablePages}
              templateDefaultPages={templateDefaultPages}
            />
          )}
          {!locked && (
            <DefaultPages
              availablePages={availablePages}
              defaultPages={defaultPages}
              onRemovePage={removeDefaultPage}
              onReorderPage={reorderDefaultPage}
            />
          )}
        </Col>
      </Row>
    </div>
  );
}

PagePicker.propTypes = {
  addDefaultPage: PropTypes.func.isRequired,
  availablePages: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired
  })).isRequired,
  clearDefaultPages: PropTypes.func.isRequired,
  clearPageLibrary: PropTypes.func.isRequired,
  defaultPages: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    key: PropTypes.string.isRequired
  })),
  formData: PropTypes.shape({
    defaultPages: PropTypes.array,
    docName: PropTypes.string,
    docOptions: PropTypes.object
  }).isRequired,
  removeDefaultPage: PropTypes.func.isRequired,
  removeLibraryPage: PropTypes.func.isRequired,
  reorderDefaultPage: PropTypes.func.isRequired,
  reorderLibraryPage: PropTypes.func.isRequired,
  setDefaultPages: PropTypes.func.isRequired,
  setLibraryPages: PropTypes.func.isRequired,
  templateDefaultPageLibrary: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired
  })),
  templateDefaultPages: PropTypes.arrayOf(PropTypes.number).isRequired,
  userCreated: PropTypes.bool
};

const mapStateToProps = ({ availablePages, formData }) => ({ availablePages, formData });
const mapDispatchToProps = {
  addDefaultPage,
  clearDefaultPages,
  clearPageLibrary,
  removeDefaultPage,
  removeLibraryPage,
  reorderDefaultPage,
  reorderLibraryPage,
  setDefaultPages,
  setLibraryPages,
};

export default connect(mapStateToProps, mapDispatchToProps)(PagePicker);
